export interface CheckedNodeLike {
  readonly key?: string;
  readonly title?: string;
  readonly category?: string;
  readonly endPoint?: string;
  readonly endpoint?: string;
  readonly checked?: boolean;
}

function layerDataHasKey(
  layerData: ReadonlySet<string> | Readonly<Record<string, unknown>>,
  key: string,
): boolean {
  if (layerData instanceof Set) {
    return layerData.has(key);
  }

  // After the Set branch, treat remaining values as a string-keyed map.
  const asMap = layerData as { readonly [layerKey: string]: unknown };
  return asMap[key] !== undefined;
}

/**
 * Keeps checked-node state in sync with map add/remove.
 * - Empty checkedNodes: selection-only (bring-to-top); do not append undefined.
 * - Existing map layer for a node key: remove that key from state.
 * - Otherwise: upsert every node in checkedNodes (folder checks include all children).
 */
export function applyCheckedNodesStateUpdate<T extends CheckedNodeLike>(
  prev: readonly T[],
  checkedNodes: readonly T[],
  layerData: ReadonlySet<string> | Readonly<Record<string, unknown>>,
): T[] {
  const cleaned = prev.filter((node): node is T => Boolean(node?.key));

  if (checkedNodes.length === 0) {
    return cleaned;
  }

  let next = [...cleaned];
  for (const node of checkedNodes) {
    if (!node?.key) {
      continue;
    }
    next = next.filter((existing) => existing.key !== node.key);
    if (!layerDataHasKey(layerData, node.key)) {
      next.push(node);
    }
  }
  return next;
}
